"use client";

import { Timer, Trophy } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";

import { Link } from "@/i18n/navigation";
import { useAuthStore } from "@/shared/model/auth-store";
import { useActiveMatch } from "@/entities/match/api/use-active-match";
import { useNextMatch } from "@/entities/match/api/use-next-match";
import { useVisibleInterval } from "@/shared/lib/hooks/use-visible-interval";
import { formatDateMsk } from "@/shared/lib/msk-time";
import { translateUi } from "@/shared/lib/ui-translate";
import { toUiLocale } from "@/shared/lib/ui-locale";

/** Человеческий остаток времени до матча: «через 12 мин», «через 2 ч 5 мин». */
export function formatTimeUntilMatch(scheduledAtUtc: string): string {
  const diffMs = Math.max(0, new Date(scheduledAtUtc).getTime() - Date.now());
  const mins = Math.floor(diffMs / 60_000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return translateUi("litHeaderUpNext.cherezDn", { days });
  if (hours > 0) return translateUi("litHeaderUpNext.cherezChMin", { hours, mins: mins % 60 });
  if (mins > 0) return translateUi("litHeaderUpNext.cherezMin", { mins });
  return translateUi("litHeaderUpNext.votVot");
}

/**
 * Плашка «что у меня сейчас» — справа от навигации.
 *
 * <p>Один слот на два состояния, а не две плашки подряд: если назначен матч,
 * показываем отсчёт до него, иначе — текущий турнир. Одновременно они и нужны
 * редко (матч почти всегда внутри активного турнира), а места в шапке нет:
 * второй постоянный элемент упирается в островок профиля.</p>
 *
 * <p>Отсчёт до матча раньше жил плиткой на дашборде. Дашборд выключен
 * фичефлагом, а это была единственная его функция, которой больше нигде нет.</p>
 *
 * <p>KAN-935: данные больше не тянутся отсюда сырым циклом. Плашка живёт на
 * КАЖДОЙ странице, поэтому её собственный `setInterval` на 30 секунд с двумя
 * запросами за тик и без гейта по видимости оказался дороже страницы турнира.
 * Теперь обе ручки — обычные запросы React Query в слое `entities/match`:
 * `/me/active-match` перестал дублироваться (тот же ключ уже спрашивает меню
 * шапки), интервал стал страховочным (180с вместо 30с), в свёрнутой вкладке
 * запросов нет вовсе, а быстрый путь идёт пушем через хаб уведомлений.</p>
 */
export function HeaderUpNextButton() {
  const tUi = useTranslations("Ui");
  const locale = toUiLocale(useLocale());
  // KAN-981: шапка на каждой странице — подписка на одно поле, а не на весь стор.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: activeMatch } = useActiveMatch(isAuthenticated);
  const { data: nextMatch } = useNextMatch(isAuthenticated);

  const active =
    activeMatch?.tournamentId && activeMatch.tournamentName
      ? { id: activeMatch.tournamentId, name: activeMatch.tournamentName }
      : null;

  // Тик раз в полминуты — ТОЛЬКО чтобы пересчитать подпись отсчёта («через
  // 12 мин» → «через 11 мин»). Сеть он не трогает: данные обновляет React Query.
  // В свёрнутой вкладке не тикает — там некому смотреть на подпись.
  const [, setTick] = useState(0);
  useVisibleInterval(() => setTick((n) => n + 1), 30_000);

  // Назначенный матч важнее общего «идёт турнир»: он про ближайшее действие.
  if (nextMatch) {
    const until = formatTimeUntilMatch(nextMatch.scheduledAtUtc);
    return (
      <Link
        href={`/tournaments/${nextMatch.tournamentId}/matches/${nextMatch.matchId}`}
        className="inline-flex min-w-0 items-center gap-1.5 px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-primary hover:bg-accent rounded-md transition-colors whitespace-nowrap"
        style={{ fontFamily: "var(--font-body), sans-serif" }}
        title={tUi("litHeaderUpNext.blizhayshiyMatch", { date: formatDateMsk(nextMatch.scheduledAtUtc, { dateStyle: "medium", timeStyle: "short" }, locale) })}
      >
        <Timer className="h-4 w-4 shrink-0" />
        <span className="hidden xl:inline truncate">{tUi("litHeaderUpNext.matchUntil", { until })}</span>
        <span className="xl:hidden">{tUi("headerUpNext.match")}</span>
      </Link>
    );
  }

  if (!active) return null;
  const shortName = active.name.length > 22 ? `${active.name.slice(0, 22)}…` : active.name;
  return (
    <Link
      href={`/tournaments/${active.id}`}
      className="inline-flex min-w-0 items-center gap-1.5 px-2 xl:px-3 py-2 text-sm xl:text-base font-medium text-primary hover:bg-accent rounded-md transition-colors whitespace-nowrap"
      style={{ fontFamily: "var(--font-body), sans-serif" }}
      title={tUi("litHeaderUpNext.tekushchiyTurnir", { name: active.name })}
    >
      <Trophy className="h-4 w-4 shrink-0" />
      <span className="hidden xl:inline truncate">{shortName}</span>
      <span className="xl:hidden">{tUi("headerUpNext.turnir")}</span>
    </Link>
  );
}

